import { Injectable } from '@angular/core';
import { NgbModal }   from '@ng-bootstrap/ng-bootstrap';

import { ApiService }                    from '../api/api.service';
import { CompanyListsTemplateComponent } from './company-lists.template.component';
import { CustomerSourceModel }           from './typings/customer-source';
import { PaymentMethodModel }            from './typings/payment-method';
import { ProductCategoryModel }          from './typings/product-category';
import { ProductGroupModel }             from './typings/product-group';
import { TemplateModel }                 from '../templates/typings/template';

@Injectable()
export class CompanyListsService {
  constructor (
    private apiService  : ApiService,
    private modalService: NgbModal
  ) {}

  addTemplate ( template : TemplateModel ) : Promise<TemplateModel> {
    const modal = this.modalService.open(CompanyListsTemplateComponent, {
      windowClass: 'lg-modal'
    });

    modal.componentInstance.template = template;

    return modal.result;
  }

  createCustomerSource ( customerSource : CustomerSourceModel ) : Promise<Array<CustomerSourceModel>> {
    return this.apiService.post('/customer_sources', customerSource, 'customer_source');
  }

  createPaymentMethod ( paymentMethod : PaymentMethodModel ) : Promise<Array<PaymentMethodModel>> {
    return this.apiService.post('/invoice_payment_methods', paymentMethod, 'invoice_payment_method');
  }

  createProductCategory ( category : ProductCategoryModel ) : Promise<ProductCategoryModel> {
    return this.apiService.post('/product_categories', category, 'product_category');
  }

  createProductGroup ( group : ProductGroupModel ) : Promise<ProductGroupModel> {
    return this.apiService.post('/product_groups', group, 'product_group');
  }

  createTemplate ( template : TemplateModel ) : Promise<TemplateModel> {
    return this.apiService.post('/templated_wordings', template, 'templated_wording');
  }

  deleteCustomerSource ( customerSource : CustomerSourceModel ) : Promise<any> {
    return this.apiService.delete('/customer_sources', customerSource.id);
  }

  deletePaymentMethod ( paymentMethod : PaymentMethodModel ) : Promise<any> {
    return this.apiService.delete('/invoice_payment_methods', paymentMethod.id);
  }

  deleteProductCategory ( category : ProductCategoryModel ) : Promise<any> {
    return this.apiService.delete('/product_category', category.id);
  }

  deleteProductGroup ( group : ProductGroupModel ) : Promise<any> {
    return this.apiService.delete('/product_group', group.name);
  }

  deleteTemplate ( template : TemplateModel ) : Promise<any> {
    return this.apiService.delete('/templated_wording', template.id);
  }

  getTemplates ( type : string ) : Promise<Array<TemplateModel>> {
    return this.apiService.get('/templated_wordings', [
      0,
      '*',
      'description',
      'asc',
      type,
      '*'
    ]);
  }

  getCustomerSources () : Promise<Array<CustomerSourceModel>> {
    return this.apiService.get('/customer_sources');
  }

  getPaymentMethods () : Promise<Array<PaymentMethodModel>> {
    return this.apiService.get('/invoice_payment_methods');
  }

  getProductCategories ( includeNone = false ) : Promise<Array<ProductCategoryModel>> {
    return this.apiService.get('/product_categories/product_category_list', includeNone);
  }

  getProductGroups () : Promise<Array<ProductGroupModel>> {
    return this.apiService.get('/product_groups');
  }

  getQuickbooksTaxRates () : Promise<Array<any>> {
    return this.apiService.get('/quickbooks_online/tax_rates')
      .then(( rates : any ) => rates.all_tax_rates || []);
  }

  saveCustomerSource ( customerSource : CustomerSourceModel ) : Promise<Array<CustomerSourceModel>> {
    return customerSource.id
      ? this.updateCustomerSource(customerSource)
      : this.createCustomerSource(customerSource);
  }

  savePaymentMethod ( paymentMethod : PaymentMethodModel ) : Promise<Array<PaymentMethodModel>> {
    return paymentMethod.id
      ? this.updatePaymentMethod(paymentMethod)
      : this.createPaymentMethod(paymentMethod);
  }

  saveProductCategory ( category : ProductCategoryModel ) : Promise<Array<ProductCategoryModel> | ProductCategoryModel> {
    return category.id
      ? this.updateProductCategory(category)
      : this.createProductCategory(category);
  }

  saveProductGroup ( group : ProductGroupModel ) : Promise<ProductGroupModel> {
    return group.created_on
      ? this.updateProductGroup(group)
      : this.createProductGroup(group);
  }

  saveTemplate ( template : TemplateModel ) : Promise<TemplateModel> {
    return template.id
      ? this.updateTemplate(template)
      : this.createTemplate(template);
  }

  updateCustomerSource ( customerSource : CustomerSourceModel ) : Promise<Array<CustomerSourceModel>> {
    return this.apiService.patch('/customer_source', customerSource, 'customer_source');
  }

  updatePaymentMethod ( paymentMethod : PaymentMethodModel ) : Promise<Array<PaymentMethodModel>> {
    return this.apiService.patch('/invoice_payment_method', paymentMethod, 'invoice_payment_method');
  }

  updateProductCategory ( category : ProductCategoryModel ) : Promise<Array<ProductCategoryModel>> {
    return this.apiService.patch('/product_category', category, 'product_category');
  }

  updateProductGroup ( group : ProductGroupModel ) : Promise<ProductGroupModel> {
    return this.apiService.patch('/product_group', group, 'product_group');
  }

  updateTemplate ( template : TemplateModel ) : Promise<TemplateModel> {
    return this.apiService.patch('/templated_wording', template, 'templated_wording');
  }
}
